import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { List, X, CaretDown, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";

const PRIMARY = [
  { to: "/technology", label: "Technology" },
  { to: "/q-core", label: "Q-Core" },
  { to: "/strategies", label: "Strategies" },
  { to: "/signals", label: "Signals" },
  { to: "/pricing", label: "License" },
];

const MORE = [
  { to: "/vip", label: "VIP Membership" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/security", label: "Security" },
  { to: "/verify", label: "Verify on Chain" },
  { to: "/partners", label: "Partners" },
  { to: "/leadership", label: "Leadership" },
  { to: "/roadmap", label: "Roadmap" },
  { to: "/about", label: "About" },
  { to: "/team", label: "Team" },
  { to: "/events", label: "Events" },
  { to: "/faq", label: "FAQ" },
  { to: "/compliance", label: "Compliance" },
  { to: "/contact", label: "Contact" },
];

const ALL = [{ to: "/", label: "Home" }, ...PRIMARY, ...MORE];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthed, isAdmin } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  const isActive = (to) => location.pathname === to;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,border,box-shadow] duration-300 ${
        scrolled ? "qs-glass border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <AnnouncementBanner />
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center shrink-0 group">
          <img src="/logo.png" alt="Quantum Scalp" className="h-9 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {PRIMARY.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-3 py-2 text-sm rounded-md transition-colors ${
                isActive(item.to) ? "text-[#2DD4BF]" : "text-[#94A3B8] hover:text-white"
              }`}
            >
              {item.label}
            </Link>
          ))}
          <div className="relative" onMouseLeave={() => setMoreOpen(false)}>
            <button
              data-testid="nav-more"
              onClick={() => setMoreOpen((v) => !v)}
              onMouseEnter={() => setMoreOpen(true)}
              className="flex items-center gap-1 px-3 py-2 text-sm rounded-md text-[#94A3B8] hover:text-white transition-colors"
            >
              More <CaretDown size={13} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            {moreOpen && (
              <div className="absolute right-0 top-full pt-2 w-52">
                <div className="qs-glass rounded-lg border border-white/10 p-2 grid grid-cols-1 gap-0.5">
                  {MORE.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="px-3 py-2 rounded-md text-sm text-[#94A3B8] hover:text-white hover:bg-white/5 transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-2">
          {isAuthed ? (
            <Link
              to={isAdmin ? "/admin" : "/dashboard"}
              data-testid="nav-dashboard-btn"
              className="px-4 py-2 text-sm rounded-full border border-white/15 text-white hover:bg-white/5 transition-colors"
            >
              {isAdmin ? "Admin" : "Dashboard"}
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="nav-signin-btn"
              className="px-4 py-2 text-sm rounded-full border border-white/15 text-white hover:bg-white/5 transition-colors"
            >
              Sign In
            </Link>
          )}
          <button
            onClick={() => navigate("/pricing")}
            data-testid="nav-access-qcore-btn"
            className="group inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full bg-[#2DD4BF] text-[#030305] hover:bg-white transition-colors"
          >
            Access Q-Core
            <ArrowRight size={15} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        <button
          className="lg:hidden text-white p-2"
          data-testid="mobile-menu-btn"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden qs-glass border-t border-white/10 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-5 py-4 grid grid-cols-2 gap-1">
            {ALL.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-2.5 rounded-md text-sm ${
                  isActive(item.to) ? "text-[#2DD4BF] bg-white/5" : "text-[#94A3B8]"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div className="px-5 pb-5 pt-1 flex flex-col gap-2">
            <Link to={isAuthed ? (isAdmin ? "/admin" : "/dashboard") : "/login"} className="text-center px-4 py-2.5 text-sm rounded-full border border-white/15 text-white">
              {isAuthed ? (isAdmin ? "Admin" : "Dashboard") : "Sign In"}
            </Link>
            <Link to="/pricing" className="text-center px-4 py-2.5 text-sm font-semibold rounded-full bg-[#2DD4BF] text-[#030305]">
              Access Q-Core
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
